import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Plus, Trash2, Upload } from "lucide-react";

export const Route = createFileRoute("/harikaradmin/events/$eventId")({
  head: () => ({ meta: [{ title: "Etkinlik düzenle — JUËN" }] }),
  component: AdminEventEditPage,
});

interface TicketTypeRow {
  id?: string;
  name: string;
  description: string;
  price_kurus: number;
  quantity: number;
  sort_order: number;
  is_active: boolean;
  coming_soon: boolean;
  shopier_payment_url: string;
  _new?: boolean;
  _delete?: boolean;
}



const eventSchema = z.object({
  name: z.string().trim().min(1, "Etkinlik adı gerekli").max(120),
  description: z.string().trim().max(2000).optional().or(z.literal("")),
  starts_at: z.string().min(1, "Tarih/saat gerekli"),
  ends_at: z.string().optional().or(z.literal("")),
  venue: z.string().trim().max(120).optional().or(z.literal("")),
});

function AdminEventEditPage() {
  const { eventId } = Route.useParams();
  const isNew = eventId === "new";
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();

  const [pageLoading, setPageLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    starts_at: "",
    ends_at: "",
    venue: "",
    is_invite_only: false,
    door_min_topup_kurus: 0,
    load_as_credit: false,
    vat_percent: 0,
    service_fee_kurus: 0,
    min_age: 0,
  });
  const [tickets, setTickets] = useState<TicketTypeRow[]>([
    { name: "Early Bird 1", description: "", price_kurus: 0, quantity: 50, sort_order: 0, is_active: true, coming_soon: false, shopier_payment_url: "", _new: true },
    { name: "Early Bird 2", description: "", price_kurus: 0, quantity: 50, sort_order: 1, is_active: true, coming_soon: false, shopier_payment_url: "", _new: true },
    { name: "Masa", description: "", price_kurus: 0, quantity: 20, sort_order: 2, is_active: true, coming_soon: false, shopier_payment_url: "", _new: true },
    { name: "Free", description: "", price_kurus: 0, quantity: 30, sort_order: 3, is_active: true, coming_soon: false, shopier_payment_url: "", _new: true },

  ]);
  

  useEffect(() => {
    if (!loading) {
      if (!user) void navigate({ to: "/login" });
      else if (!isAdmin) void navigate({ to: "/dashboard" });
    }
  }, [loading, user, isAdmin, navigate]);

  useEffect(() => {
    if (isNew || !isAdmin) return;
    void (async () => {
      const [{ data: ev, error: evErr }, { data: tk, error: tkErr }] = await Promise.all([
        supabase.from("events").select("*").eq("id", eventId).maybeSingle(),
        supabase.from("ticket_types").select("*").eq("event_id", eventId).order("sort_order"),
      ]);
      if (evErr || !ev) {
        toast.error(evErr?.message ?? "Etkinlik bulunamadı");
        void navigate({ to: "/harikaradmin/events" });
        return;
      }
      if (tkErr) toast.error(tkErr.message);
      setForm({
        name: ev.name,
        description: ev.description ?? "",
        starts_at: toLocalInput(ev.starts_at),
        ends_at: ev.ends_at ? toLocalInput(ev.ends_at) : "",
        venue: ev.venue ?? "",
        is_invite_only: (ev as { is_invite_only?: boolean }).is_invite_only ?? false,
        door_min_topup_kurus: (ev as { door_min_topup_kurus?: number }).door_min_topup_kurus ?? 0,
        load_as_credit: (ev as { load_as_credit?: boolean }).load_as_credit ?? false,
        vat_percent: Number((ev as { vat_percent?: number | string }).vat_percent ?? 0),
        service_fee_kurus: (ev as { service_fee_kurus?: number }).service_fee_kurus ?? 0,
        min_age: (ev as { min_age?: number }).min_age ?? 0,
      });
      setCoverUrl(ev.cover_image_url);
      setTickets(
        (tk ?? []).map((t) => ({
          id: t.id,
          name: t.name,
          description: t.description ?? "",
          price_kurus: t.price_kurus,
          quantity: t.quantity,
          sort_order: t.sort_order,
          is_active: (t as { is_active?: boolean }).is_active ?? true,
          coming_soon: (t as { coming_soon?: boolean }).coming_soon ?? false,
          shopier_payment_url: (t as { shopier_payment_url?: string | null }).shopier_payment_url ?? "",
        })),

      );
      setPageLoading(false);
    })();
  }, [isNew, eventId, isAdmin, navigate]);

  const handleUpload = async (file: File) => {
    if (!user) return;
    setUploading(true);
    const ext = file.name.split(".").pop() ?? "jpg";
    const path = `${user.id}/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("event-images").upload(path, file, { upsert: false });
    if (error) {
      toast.error(error.message);
      setUploading(false);
      return;
    }
    const { data } = supabase.storage.from("event-images").getPublicUrl(path);
    setCoverUrl(data.publicUrl);
    setUploading(false);
    toast.success("Görsel yüklendi");
  };

  const updateTicket = (idx: number, patch: Partial<TicketTypeRow>) => {
    setTickets((arr) => arr.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };
  const addTicket = () => {
    setTickets((arr) => [
      ...arr,
      { name: "", description: "", price_kurus: 0, quantity: 0, sort_order: arr.length, is_active: true, coming_soon: false, shopier_payment_url: "", _new: true },
    ]);
  };
  const removeTicket = (idx: number) => {
    setTickets((arr) => {
      const t = arr[idx];
      if (t._new) return arr.filter((_, i) => i !== idx);
      return arr.map((x, i) => (i === idx ? { ...x, _delete: true } : x));
    });
  };


  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const parsed = eventSchema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Bilgileri kontrol edin");
      return;
    }
    const activeTickets = tickets.filter((t) => !t._delete);
    if (!form.is_invite_only && activeTickets.length === 0) {
      toast.error("En az bir bilet türü gerekli");
      return;
    }
    for (const t of activeTickets) {
      if (!t.name.trim()) {
        toast.error("Tüm bilet türlerinin adı olmalı");
        return;
      }
    }

    setSaving(true);
    const payload = {
      name: parsed.data.name,
      description: parsed.data.description || null,
      starts_at: new Date(parsed.data.starts_at).toISOString(),
      ends_at: parsed.data.ends_at ? new Date(parsed.data.ends_at).toISOString() : null,
      venue: parsed.data.venue || null,
      cover_image_url: coverUrl,
      is_invite_only: form.is_invite_only,
      door_min_topup_kurus: Math.max(0, Math.round(form.door_min_topup_kurus)),
      load_as_credit: form.load_as_credit,
      vat_percent: Math.max(0, Math.min(100, Number(form.vat_percent) || 0)),
      service_fee_kurus: Math.max(0, Math.round(form.service_fee_kurus)),
      min_age: Math.max(0, Math.min(99, Math.round(Number(form.min_age) || 0))),
    };

    let currentId = eventId;
    if (isNew) {
      const { data, error } = await supabase
        .from("events")
        .insert({ ...payload, created_by: user!.id })
        .select("id")
        .single();
      if (error || !data) {
        toast.error(error?.message ?? "Kaydetme hatası");
        setSaving(false);
        return;
      }
      currentId = data.id;
    } else {
      const { error } = await supabase.from("events").update(payload).eq("id", eventId);
      if (error) {
        toast.error(error.message);
        setSaving(false);
        return;
      }
    }

    // Tickets
    const toDelete = tickets.filter((t) => t._delete && t.id).map((t) => t.id!);
    const toInsert = tickets
      .filter((t) => t._new && !t._delete)
      .map((t, i) => ({
        event_id: currentId,
        name: t.name.trim(),
        description: t.description || null,
        price_kurus: Math.round(t.price_kurus),
        quantity: Math.round(t.quantity),
        sort_order: i,
        is_active: t.is_active,
        coming_soon: t.coming_soon,
        shopier_payment_url: t.shopier_payment_url?.trim() || null,
      }));

    const toUpdate = tickets.filter((t) => !t._new && !t._delete && t.id);

    if (toDelete.length) {
      const { error } = await supabase.from("ticket_types").delete().in("id", toDelete);
      if (error) toast.error("Bilet silme hatası: " + error.message);
    }
    if (toInsert.length) {
      const { error } = await supabase.from("ticket_types").insert(toInsert);
      if (error) toast.error("Bilet ekleme hatası: " + error.message);
    }
    for (const t of toUpdate) {
      const { error } = await supabase
        .from("ticket_types")
        .update({
          name: t.name.trim(),
          description: t.description || null,
          price_kurus: Math.round(t.price_kurus),
          quantity: Math.round(t.quantity),
          sort_order: t.sort_order,
          is_active: t.is_active,
          coming_soon: t.coming_soon,
          shopier_payment_url: t.shopier_payment_url?.trim() || null,
        })

        .eq("id", t.id!);
      if (error) toast.error("Bilet güncelleme hatası: " + error.message);
    }


    setSaving(false);
    toast.success(isNew ? "Etkinlik oluşturuldu" : "Değişiklikler kaydedildi");
    void navigate({ to: "/harikaradmin/events" });
  };

  if (loading || !isAdmin || pageLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Link to="/harikaradmin/events" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Etkinliklere dön
        </Link>
        <h1 className="mb-6 text-3xl font-bold tracking-tight md:text-4xl">
          {isNew ? "Yeni etkinlik" : "Etkinliği düzenle"}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Cover */}
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <Label>Kapak görseli</Label>
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <div className="h-32 w-48 overflow-hidden rounded-lg bg-muted">
                {coverUrl ? (
                  <img src={coverUrl} alt="Kapak" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">Görsel yok</div>
                )}
              </div>
              <div>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted">
                  <Upload className="h-4 w-4" />
                  {uploading ? "Yükleniyor..." : coverUrl ? "Görseli değiştir" : "Görsel yükle"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void handleUpload(f);
                      e.target.value = "";
                    }}
                  />
                </label>
                {coverUrl && (
                  <button type="button" onClick={() => setCoverUrl(null)} className="ml-2 text-sm text-muted-foreground hover:text-destructive">
                    Kaldır
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Basic info */}
          <div className="space-y-5 rounded-2xl border border-border bg-card p-6 shadow-card">
            <div className="space-y-2">
              <Label htmlFor="name">Etkinlik adı *</Label>
              <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="starts_at">Başlangıç *</Label>
                <Input
                  id="starts_at"
                  type="datetime-local"
                  value={form.starts_at}
                  onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ends_at">Bitiş (opsiyonel)</Label>
                <Input
                  id="ends_at"
                  type="datetime-local"
                  value={form.ends_at}
                  onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="venue">Mekan</Label>
              <Input id="venue" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} placeholder="JUËN, İstanbul" />
            </div>

            <div className="space-y-2 rounded-xl border-2 border-primary/40 bg-primary/5 p-4">
              <Label htmlFor="min_age" className="text-base font-semibold text-primary">
                Yaş sınırı
              </Label>
              <Input
                id="min_age"
                type="number"
                min={0}
                max={99}
                step="1"
                value={form.min_age.toString()}
                onChange={(e) => setForm({ ...form, min_age: parseInt(e.target.value || "0", 10) })}
                placeholder="0"
                className="max-w-[160px] text-lg font-semibold"
              />
              <p className="text-xs text-muted-foreground">
                0 ise sınır yok. Örn. 21 girersen 21 yaşından küçük üyeler bilet alamaz.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Açıklama</Label>
              <Textarea
                id="description"
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
          </div>

          {/* Invite mode */}
          <div className="space-y-4 rounded-2xl border border-border bg-card p-6 shadow-card">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">Davetiye modu</h2>
                <p className="text-sm text-muted-foreground">
                  Açıldığında bilet satılmaz; üyeler tek tıkla 1 ücretsiz davetiye alır ve girişte QR ile okutur.
                </p>
              </div>
              <Switch
                checked={form.is_invite_only}
                onCheckedChange={(v) => setForm({ ...form, is_invite_only: v })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="door_topup">Kapıda zorunlu min. bakiye (TL)</Label>
              <Input
                id="door_topup"
                type="number"
                min={0}
                step="1"
                value={(form.door_min_topup_kurus / 100).toString()}
                onChange={(e) =>
                  setForm({ ...form, door_min_topup_kurus: Math.round(parseFloat(e.target.value || "0") * 100) })
                }
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">
                0 ise kapıda zorunluluk yok. Belirlersen QR okutulduğunda görevliye uyarı olarak gösterilir.
              </p>
            </div>
          </div>

          {/* Load-as-credit mode */}
          <div className="space-y-2 rounded-2xl border border-border bg-card p-6 shadow-card">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">Ödeme J olarak yüklensin</h2>
                <p className="text-sm text-muted-foreground">
                  Açıldığında bilet ödemesi tamamlandığında ödenen tutar kadar J kullanıcının hesabına otomatik yüklenir (1 TL = 1 J). Bilet de oluşur — harcama limiti olarak kullanılır.
                </p>
          </div>

          {/* KDV & Hizmet bedeli */}
          <div className="space-y-4 rounded-2xl border border-border bg-card p-6 shadow-card">
            <div>
              <h2 className="text-lg font-semibold">KDV & Hizmet bedeli</h2>
              <p className="text-sm text-muted-foreground">
                Bilet fiyatına ek olarak ödeme adımında gösterilir. Shopier toplamıyla eşleşmesi için buradan ayarla.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label className="text-xs">KDV (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={form.vat_percent.toString()}
                  onChange={(e) => setForm({ ...form, vat_percent: parseFloat(e.target.value || "0") })}
                />
                <p className="mt-1 text-xs text-muted-foreground">Bilet ara toplamı üzerinden hesaplanır.</p>
              </div>
              <div>
                <Label className="text-xs">Hizmet bedeli (TL, sabit)</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={(form.service_fee_kurus / 100).toString()}
                  onChange={(e) => setForm({ ...form, service_fee_kurus: Math.round(parseFloat(e.target.value || "0") * 100) })}
                />
                <p className="mt-1 text-xs text-muted-foreground">Sipariş başına sabit tutar.</p>
              </div>
            </div>
          </div>


              <Switch
                checked={form.load_as_credit}
                onCheckedChange={(v) => setForm({ ...form, load_as_credit: v })}
              />
            </div>
          </div>


          {/* Tickets */}
          {!form.is_invite_only && (
          <div className="space-y-4 rounded-2xl border border-border bg-card p-6 shadow-card">

            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Bilet türleri</h2>
                <p className="text-sm text-muted-foreground">Standart, VIP, Erken Kuş gibi türler ekleyin.</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addTicket}>
                <Plus className="mr-1.5 h-4 w-4" /> Tür ekle
              </Button>
            </div>

            <div className="space-y-3">
              {tickets.map((t, idx) =>
                t._delete ? null : (
                  <div key={idx} className="rounded-xl border border-border p-4">
                    <div className="grid gap-3 md:grid-cols-[1fr_120px_120px_auto]">
                      <div>
                        <Label className="text-xs">Ad</Label>
                        <Input
                          value={t.name}
                          onChange={(e) => updateTicket(idx, { name: e.target.value })}
                          placeholder="Standart"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Fiyat (TL)</Label>
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          value={(t.price_kurus / 100).toString()}
                          onChange={(e) => updateTicket(idx, { price_kurus: Math.round(parseFloat(e.target.value || "0") * 100) })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Kontenjan</Label>
                        <Input
                          type="number"
                          min={0}
                          value={t.quantity.toString()}
                          onChange={(e) => updateTicket(idx, { quantity: parseInt(e.target.value || "0", 10) })}
                        />
                      </div>
                      <div className="flex items-end">
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeTicket(idx)} className="text-muted-foreground hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2">
                      <Label className="text-xs">Açıklama (opsiyonel)</Label>
                      <Input
                        value={t.description}
                        onChange={(e) => updateTicket(idx, { description: e.target.value })}
                        placeholder="Bu türde neler dahil?"
                      />
                    </div>
                    <div className="mt-2">
                      <Label className="text-xs">Shopier ödeme linki (opsiyonel)</Label>
                      <Input
                        value={t.shopier_payment_url}
                        onChange={(e) => updateTicket(idx, { shopier_payment_url: e.target.value })}
                        placeholder="https://www.shopier.com/..."
                      />
                      <p className="mt-1 text-[11px] text-muted-foreground">Shopier panelinde bu bilet için bir ürün oluştur, ödeme linkini buraya yapıştır.</p>
                    </div>
                    <div className="mt-3 flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2">
                      <div>
                        <div className="text-sm font-medium">{t.is_active ? "Aktif" : "Pasif"}</div>
                        <div className="text-xs text-muted-foreground">
                          Pasif bilet türleri müşterilere gösterilmez.
                        </div>
                      </div>
                      <Switch
                        checked={t.is_active}
                        onCheckedChange={(v) => updateTicket(idx, { is_active: v })}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between rounded-lg bg-muted/40 px-3 py-2">
                      <div>
                        <div className="text-sm font-medium">Satışı yakında</div>
                        <div className="text-xs text-muted-foreground">
                          Açıkken kullanıcılar bileti göremez ama "Satışı yakında" etiketi görünür.
                        </div>
                      </div>
                      <Switch
                        checked={t.coming_soon}
                        onCheckedChange={(v) => updateTicket(idx, { coming_soon: v })}
                      />
                    </div>
                  </div>
                ),
              )}

            </div>
          </div>
          )}




          <div className="flex gap-3">
            <Button type="submit" disabled={saving} className="h-12 flex-1 text-base">
              {saving ? "Kaydediliyor..." : isNew ? "Etkinliği oluştur" : "Değişiklikleri kaydet"}
            </Button>
            <Button asChild type="button" variant="outline" className="h-12">
              <Link to="/harikaradmin/events">İptal</Link>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
